import React, {useState, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import {
  BarChart,
  Bar,
  Treemap,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  PieChart as PieChartIcon,
  Filter,
  Settings,
  BarChart2,
  Download,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import { rgb } from 'd3-color';
import { useDashboardQuery } from '../hooks/useDashboardQuery';
import { getCurrentUserId } from '../utils/userUtils';
import styled from 'styled-components';
import {
  Select,
  Tooltip as AntdTooltip,
  Drawer,
  Divider,
  Checkbox,
  ColorPicker,
  InputNumber,
} from 'antd';

// NEW: Import Lottie and the Infinity Loop JSON animation
import Lottie from 'lottie-react';
import InfinityLoop from '../img/Infinity_Loop.json';

const USER_ID = getCurrentUserId();

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10,
    },
  },
};

// Utility Functions

// Format Number with Abbreviation
const formatNumber = (num, format = 'currency') => {
  const number = typeof num === 'number' ? num : Number(num);
  if (isNaN(number)) {
    return 'N/A';
  }

  const absNumber = Math.abs(number);

  const abbreviateNumber = (number) => {
    if (absNumber >= 1.0e+12) {
      return (number / 1.0e+12).toFixed(1).replace(/\.0$/, '') + 'T';
    } else if (absNumber >= 1.0e+9) {
      return (number / 1.0e+9).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (absNumber >= 1.0e+6) {
      return (number / 1.0e+6).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (absNumber >= 1.0e+3) {
      return (number / 1.0e+3).toFixed(1).replace(/\.0$/, '') + 'K';
    } else {
      return number.toLocaleString();
    }
  };

  // Handle different format types
  switch (format) {
    case 'currency':
      return '$' + abbreviateNumber(number);
    
    case 'full':
      return new Intl.NumberFormat('en-US', {
        style: format === 'currency' ? 'currency' : 'decimal',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(number);
    
    case 'number':
      return abbreviateNumber(number);
    
    case 'percentage':
      return abbreviateNumber(number) + '%';
    
    case 'full-percentage':
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(number) + '%';
    
    default:
      return '$' + abbreviateNumber(number);
  }
};

// Abbreviate Long Names
const abbreviateName = (name, maxLength = 10) => {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

// Styled Select Component using styled-components
const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 12px !important;
    border: 1px solid rgba(59, 130, 246, 0.2) !important;
    background: rgba(255, 255, 255, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    padding: 4px 16px !important;
    height: 42px !important;
    display: flex !important;
    align-items: center !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 10px 40px -5px rgba(0, 0, 0, 0.1) !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: rgba(59, 130, 246, 0.3) !important;
    box-shadow: 0 20px 50px -10px rgba(59, 130, 246, 0.2) !important;
  }

  .ant-select-selection-item {
    font-weight: 500 !important;
    color: #1e40af !important;
    line-height: 34px !important;
  }

  &.ant-select {
    width: 120px !important;
    height: 42px !important;
  }
`;

// Component Definitions

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const fullName = payload[0]?.payload?.fullName || label;
    return (
      <div
        className="bg-white/90 backdrop-blur-md border border-blue-200/80 p-3 rounded-xl
          shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
          ring-1 ring-blue-100/30"
      >
        <p className="font-semibold text-gray-800">{fullName}</p>
        {payload.map((entry, index) => {
          // Determine the format based on the data key
          let formattedValue = entry.value;
          if (entry.dataKey === 'rate' || 
              entry.name === 'rate' || 
              entry.dataKey === 'count' || 
              entry.name === 'count' ||
              entry.dataKey === 'participants' || 
              entry.name === 'participants') {
            // For percentage or count values, don't use currency formatting
            formattedValue = Math.round(entry.value);
            // Add % symbol for rate values
            if (entry.dataKey === 'rate' || entry.name === 'rate') {
              formattedValue = `${formattedValue}%`;
            }
          } else if (typeof entry.value === 'number') {
            formattedValue = formatNumber(entry.value, 'full');
          }

          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formattedValue}`}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

// StatCard Component with Tooltip
const StatCard = ({ title, value, icon: Icon, change, format = 'currency' }) => (
  <motion.div
    variants={itemVariants}
    className="bg-white/70 backdrop-blur-lg p-6 rounded-2xl transition-all duration-300
      border border-blue-200/80 shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
      hover:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.2)] hover:border-blue-300/80 w-full
      ring-1 ring-blue-100/30"
  >
    <div className="flex flex-col space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-500/80">{title}</p>
          <AntdTooltip title={formatNumber(value, 'full')}>
            <p className="text-2xl font-bold text-gray-800 mt-1 cursor-pointer">
              {formatNumber(value, format)}
            </p>
          </AntdTooltip>
        </div>
        <div
          className={`p-3 rounded-xl ${
            change >= 0 ? 'bg-green-50/50 text-green-600' : 'bg-red-50/50 text-red-600'
          }`}
        >
          <Icon size={24} className="w-6 h-6" />
        </div>
      </div>
      {change !== null && change !== undefined && !isNaN(change) && (
        <div
          className={`flex items-center ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}
        >
          {change >= 0 ? (
            <TrendingUp size={16} className="mr-1" />
          ) : (
            <TrendingDown size={16} className="mr-1" />
          )}
          <span className="text-sm font-medium">
            {change >= 0 ? '+' : ''}
            {change.toFixed(2)}% from last year
          </span>
        </div>
      )}
    </div>
  </motion.div>
);

// FilterDrawer Component
const FilterDrawer = ({ visible, onClose, data, activeFilters, onFilterChange, title }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique company names from data and sort alphabetically
  const companies = useMemo(() => {
    if (!data) return [];
    const uniqueCompanies = [
      ...new Set(
        data.map((item) => {
          return item.fullName || item.name || '';
        })
      ),
    ]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return uniqueCompanies;
  }, [data]);

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return companies;
    return companies.filter((company) =>
      company.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  // Handle checkbox changes while preserving other selections
  const handleCheckboxChange = (checkedValues) => {
    // Get the set of companies that are currently visible/filtered
    const filteredSet = new Set(filteredCompanies);
    
    // Keep selections of companies that aren't currently filtered/visible
    const selectionsToKeep = activeFilters.filter(company => !filteredSet.has(company));
    
    // Add the new selections from the filtered/visible companies
    const newSelections = [
      ...selectionsToKeep,
      ...checkedValues.filter(company => filteredSet.has(company))
    ];
    
    onFilterChange(newSelections);
  };

  // Handle Select All for filtered companies only
  const handleSelectAll = () => {
    // Keep existing selections for companies not in the filtered view
    const filteredSet = new Set(filteredCompanies);
    const selectionsToKeep = activeFilters.filter(company => !filteredSet.has(company));
    
    // Combine with all filtered companies
    const newSelections = [
      ...selectionsToKeep,
      ...filteredCompanies
    ];
    
    onFilterChange([...new Set(newSelections)]); // Remove duplicates
  };

  // Handle Clear All for filtered companies only
  const handleClearAll = () => {
    // Keep only the selections that aren't in the current filtered view
    const filteredSet = new Set(filteredCompanies);
    const remainingSelections = activeFilters.filter(company => !filteredSet.has(company));
    onFilterChange(remainingSelections);
  };

  return (
    <Drawer
      title={`Filter ${title}`}
      placement="right"
      onClose={onClose}
      open={visible}
      width={300}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50
                rounded-xl transition-all duration-200 hover:bg-blue-100
                hover:text-blue-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 focus:ring-offset-2 border border-blue-100"
          >
            Select All
          </button>
          <button
            onClick={handleClearAll}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50
                rounded-xl transition-all duration-200 hover:bg-red-100
                hover:text-red-700 focus:outline-none focus:ring-2
                focus:ring-red-500 focus:ring-offset-2 border border-red-100"
          >
            Clear All
          </button>
        </div>

        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search companies..."
            className="w-full px-4 py-2 border border-gray-200 rounded-xl
                focus:outline-none focus:ring-2 focus:ring-blue-500
                focus:border-transparent transition-all duration-200
                placeholder-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2
                  text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-500 mb-3">
            {activeFilters.length} of {companies.length} selected
            {searchTerm && ` (${filteredCompanies.length} matching)`}
          </div>

          <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
            <Checkbox.Group
              value={activeFilters}
              onChange={handleCheckboxChange}
              className="flex flex-col gap-2.5"
            >
              {filteredCompanies.map((company) => (
                <label
                  key={company}
                  className="flex items-center p-3 rounded-xl transition-all duration-200
                      hover:bg-blue-50/50 cursor-pointer group border border-transparent
                      hover:border-blue-100"
                >
                  <Checkbox value={company} className="text-gray-700" />
                  <span className="ml-2 text-gray-700 group-hover:text-blue-600
                      transition-colors duration-200">
                    {company}
                  </span>
                </label>
              ))}
            </Checkbox.Group>
          </div>

          {filteredCompanies.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No companies match your search
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

// Sort Options Enum
const SORT_OPTIONS = {
  ALPHABETICAL_ASC: 'alphabetical_asc',
  ALPHABETICAL_DESC: 'alphabetical_desc',
  VALUE_ASC: 'value_ascending',
  VALUE_DESC: 'value_descending',
};

// ChartSettingsDrawer Component
const ChartSettingsDrawer = ({
  visible,
  onClose,
  currentColor,
  onColorChange,
  itemsPerPage,
  onItemsPerPageChange,
  sortOption,
  onSortOptionChange,
}) => {
  return (
    <Drawer
      title="Chart Settings"
      placement="right"
      onClose={onClose}
      open={visible}
      width={300}
    >
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Bar Color</h3>
          <ColorPicker
            value={currentColor}
            onChange={(color) => onColorChange(color.toHexString())}
            className="w-full"
          />
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Sort By</h3>
          <Select
            value={sortOption}
            onChange={onSortOptionChange}
            className="w-full"
            options={[
              { value: SORT_OPTIONS.ALPHABETICAL_ASC, label: 'Alphabetical (A-Z)' },
              { value: SORT_OPTIONS.ALPHABETICAL_DESC, label: 'Alphabetical (Z-A)' },
              { value: SORT_OPTIONS.VALUE_ASC, label: 'Value (Low to High)' },
              { value: SORT_OPTIONS.VALUE_DESC, label: 'Value (High to Low)' },
            ]}
          />
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Items Per Page</h3>
          <InputNumber
            min={1}
            max={50}
            value={itemsPerPage}
            onChange={onItemsPerPageChange}
            className="w-full"
          />
          <p className="mt-1 text-sm text-gray-500">
            Choose how many items to display per page (1-50)
          </p>
        </div>
      </div>
    </Drawer>
  );
};

// Sort Data Function
const sortData = (data, sortOption, valueKey) => {
  if (!data || !Array.isArray(data)) return data;

  const sortedData = [...data];
  
  const getValue = (item) => {
    const value = item[valueKey];
    return value === null || value === undefined ? -Infinity : value;
  };

  switch (sortOption) {
    case SORT_OPTIONS.ALPHABETICAL_ASC:
      return sortedData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    case SORT_OPTIONS.ALPHABETICAL_DESC:
      return sortedData.sort((a, b) => (b.name || '').localeCompare(a.name || ''));

    case SORT_OPTIONS.VALUE_ASC:
      return sortedData.sort((a, b) => getValue(a) - getValue(b));

    case SORT_OPTIONS.VALUE_DESC:
      return sortedData.sort((a, b) => getValue(b) - getValue(a));

    default:
      return sortedData.sort((a, b) => getValue(b) - getValue(a)); // Default to VALUE_DESC
  }
};

// ChartCard Component
const ChartCard = ({ title, description, children, className, data, valueKey }) => {
  const [statsVisible, setStatsVisible] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [barColor, setBarColor] = useState('#3B82F6');
  const [localItemsPerPage, setLocalItemsPerPage] = useState(8);
  const [sortOption, setSortOption] = useState(SORT_OPTIONS.VALUE_DESC);
  const chartRef = useRef(null);

  // Initialize filters when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const companyNames = data.map(item => item.fullName || item.name).filter(Boolean);
      if (!_.isEqual(new Set(activeFilters), new Set(companyNames))) {
        setActiveFilters(companyNames);
      }
    }
  }, [data]);

  // Filter and sort the data
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    const filteredData = data.filter((item) =>
      activeFilters.includes(item.fullName || item.name)
    );

    return sortData(filteredData, sortOption, valueKey);
  }, [data, activeFilters, sortOption, valueKey]);

  const downloadChart = () => {
    if (!chartRef.current) return;

    const chartContainer = chartRef.current.querySelector('.recharts-wrapper');
    if (!chartContainer) return;

    const canvas = document.createElement('canvas');
    const svg = chartContainer.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const width = svg.getAttribute('width') || 800;
    const height = svg.getAttribute('height') || 600;

    canvas.width = width;
    canvas.height = height;

    const img = new Image();
    img.onload = () => {
      const context = canvas.getContext('2d');
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, width, height);
      context.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.download = `${title.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <motion.div
      variants={itemVariants}
      className={`bg-white/70 backdrop-blur-lg p-6 rounded-2xl transition-all duration-300 
        border border-blue-200/80 shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)] 
        hover:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.2)] hover:border-blue-300/80
        ring-1 ring-blue-100/30 ${className}`}
      ref={chartRef}
    >
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-gray-600 italic">{description}</p>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 sm:flex-nowrap">
          {data && data.length > 0 && (
            <button
              onClick={() => setStatsVisible(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600
                bg-white backdrop-blur-md rounded-xl transition-all duration-200
                hover:bg-blue-50 border border-blue-200 w-full sm:w-auto
                hover:border-blue-300 shadow-sm hover:shadow-md
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <BarChart2 className="w-4 h-4" />
              Stats
            </button>
          )}
          <button
            onClick={() => setFilterVisible(true)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 
                bg-white backdrop-blur-md rounded-xl transition-all duration-200 
                hover:bg-blue-50 border border-blue-200 w-full sm:w-auto
                hover:border-blue-300 shadow-sm hover:shadow-md
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {activeFilters.length !== (data?.length || 0) && (
              <span className="flex items-center justify-center w-5 h-5 text-xs font-semibold
                  bg-blue-100 text-blue-600 rounded-full">
                {activeFilters.length}
              </span>
            )}
          </button>
          <button
            onClick={downloadChart}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600
                bg-white backdrop-blur-md rounded-xl transition-all duration-200
                hover:bg-blue-50 border border-blue-200 w-full sm:w-auto
                hover:border-blue-300 shadow-sm hover:shadow-md
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={() => setSettingsVisible(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600
                bg-white backdrop-blur-md rounded-xl transition-all duration-200
                hover:bg-blue-50 border border-blue-200 w-full sm:w-auto
                hover:border-blue-300 shadow-sm hover:shadow-md
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Render Chart */}
      {typeof children === 'function'
        ? children(processedData, barColor, localItemsPerPage)
        : children}

      {/* Drawers */}
      {data && (
        <>
          <StatsDrawer
            visible={statsVisible}
            onClose={() => setStatsVisible(false)}
            data={processedData}
            title={title}
            valueKey={valueKey}
          />
          <FilterDrawer
            visible={filterVisible}
            onClose={() => setFilterVisible(false)}
            data={data}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            title={title}
          />
          <ChartSettingsDrawer
            visible={settingsVisible}
            onClose={() => setSettingsVisible(false)}
            currentColor={barColor}
            onColorChange={setBarColor}
            itemsPerPage={localItemsPerPage}
            onItemsPerPageChange={setLocalItemsPerPage}
            sortOption={sortOption}
            onSortOptionChange={setSortOption}
          />
        </>
      )}
    </motion.div>
  );
};

// Loading Spinner Component
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

// NEW: Updated LoadingSpinner using the Lottie Infinity Loop animation
const LoadingSpinner = () => (
  <LoadingContainer>
    <Lottie 
      animationData={InfinityLoop} 
      loop={true} 
      style={{ height: "200px", width: "200px" }} 
    />
  </LoadingContainer>
);

// Custom Treemap Tooltip
const CustomTreemapTooltip = ({ active, payload, totalSize }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentage = ((data.size / totalSize) * 100).toFixed(2);
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-blue-200 p-3 rounded-lg shadow-lg"
      >
        <p className="font-bold text-blue-600">{data.name}</p>
        <p className="text-gray-700">{`Value: ${formatNumber(data.size, 'currency')}`}</p>
        <p className="text-gray-700">{`Percentage: ${percentage}%`}</p>
      </motion.div>
    );
  }
  return null;
};


// Calculate Total Size for Treemap
const calculateTotalSize = (data) => {
  return data.reduce((total, item) => total + item.size, 0);
};

// Custom Treemap Content
const CustomTreemapContent = ({
  x,
  y,
  width,
  height,
  name,
  depth,
  index,
  payload,
}) => {
  // Early return if dimensions are too small
  if (width < 1 || height < 1) return null;

  const backgroundColor = treeMapColors[index % treeMapColors.length];
  const bgColor = rgb(backgroundColor);
  const luminance = (0.299 * bgColor.r + 0.587 * bgColor.g + 0.114 * bgColor.b) / 255;
  const textColor = luminance > 0.6 ? '#1e3a8a' : '#fff';

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: backgroundColor,
          stroke: '#fff',
          strokeWidth: 2,
          strokeOpacity: 1 / (depth + 1e-10),
          transition: 'all 0.3s ease-in-out',
        }}
      />
      {width > 30 && height > 30 && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill={textColor}
          fontSize={14}
          fontWeight="medium"
          style={{
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {name}
        </text>
      )}
    </g>
  );
};

// Color Scheme for Treemap
const treeMapColors = [
  '#1E40AF',
  '#3B82F6',
  '#60A5FA',
  '#93C5FD',
  '#BFDBFE',
  '#DBEAFE',
];

// Error Display Component
const ErrorDisplay = ({ message }) => (
  <div className="min-h-[50vh] flex flex-col items-center justify-center p-8">
    <div className="bg-white/70 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center
      border border-blue-200/80 shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
      ring-1 ring-blue-100/30">
      <div className="mb-6">
        <svg
          className="mx-auto h-12 w-12 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Unable to Load Data
      </h3>
      <p className="text-gray-600 mb-6">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Try Again
      </button>
    </div>
  </div>
);

// NavigationMenu Component for FloatingNav
const NavigationMenu = ({ sections, selectedSection, onSectionChange }) => {
  return (
    <div className="overflow-y-auto max-h-[calc(100vh-200px)] bg-white backdrop-blur-md 
        rounded-xl shadow-lg border border-blue-200/80 p-2">
      {sections.map((section) => (
        <button
          key={section.title}
          onClick={() => onSectionChange(section)}
          className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200
                ${
                  selectedSection?.title === section.title
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-blue-50/50 text-gray-700'
                }`}
        >
          {section.title}
        </button>
      ))}
    </div>
  );
};

// FloatingNav Component
const FloatingNav = ({ sections, selectedYear, availableYears, onYearChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(sections[0]);

  const handleSectionChange = (section) => {
    setSelectedSection(section);
    section.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setIsOpen(false);
  };

  return (
    <motion.div
      className="fixed top-6 right-6 z-50 flex items-center gap-4 bg-white/80 backdrop-blur-md p-3 rounded-xl shadow-lg border border-blue-200/80"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center">
        <StyledSelect
          value={selectedYear}
          onChange={onYearChange}
          options={(Array.isArray(availableYears) ? availableYears : []).map((year) => ({
            value: year,
            label: year.toString(),
          }))}
        />
      </div>

      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-blue-600/90 backdrop-blur-lg text-white rounded-xl
                shadow-lg hover:shadow-xl transition-all duration-200
                border border-blue-400/30 h-[42px] px-4 w-[120px]
                flex items-center justify-between"
        >
          <span className="font-medium">Navigate</span>
          <motion.svg
            className="w-5 h-5 ml-2"
            animate={{ rotate: isOpen ? 180 : 0 }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </motion.svg>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute right-0 mt-3 w-64"
            >
              <NavigationMenu
                sections={sections}
                selectedSection={selectedSection}
                onSectionChange={handleSectionChange}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// StatsDrawer Component
const StatsDrawer = ({ visible, onClose, data, title, valueKey }) => {
  const getFormatType = (title) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('revenue') || titleLower.includes('aum') || titleLower.includes('balance')) {
      return 'currency';
    }
    if (titleLower.includes('rate') || titleLower.includes('percentage') || 
        titleLower.includes('registered') || titleLower.includes('opting') || 
        titleLower.includes('withdrawal') || titleLower.includes('portfolio') ||
        titleLower.includes('models') || titleLower.includes('deferral')) {
      return 'percentage';
    }
    if (titleLower.includes('participants') || titleLower.includes('plans') || 
        titleLower.includes('touchpoints') || titleLower.includes('requirements') ||
        titleLower.includes('count')) {
      return 'number';
    }
    if (titleLower.includes('time') || titleLower.includes('hours')) {
      return 'hours';
    }
    return 'number';
  };

  const formatStatValue = (value, format) => {
    switch (format) {
      case 'currency':
        return formatNumber(value, 'currency');
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'hours':
        return `${value.toFixed(2)} hours`;
      case 'number':
        return formatNumber(value, 'number');
      default:
        return formatNumber(value, 'number');
    }
  };

  const calculateStats = () => {
    if (!data || !data.length) return null;

    const format = getFormatType(title);

    // Extract numeric values with company names
    const valuesWithNames = data
      .map((item) => ({
        value: Number(item[valueKey]),
        name: item.fullName || item.name,
      }))
      .filter((item) => !isNaN(item.value));

    if (!valuesWithNames.length) return null;

    // Sort values for percentile calculations
    const sortedValues = [...valuesWithNames].sort((a, b) => a.value - b.value);

    // Calculate mean
    const mean =
      valuesWithNames.reduce((sum, item) => sum + item.value, 0) / valuesWithNames.length;

    // Calculate median and quartiles
    const getPercentile = (arr, percentile) => {
      const index = Math.ceil((percentile / 100) * arr.length) - 1;
      return arr[index];
    };

    // Calculate standard deviation
    const variance = valuesWithNames.reduce(
      (sum, item) => sum + Math.pow(item.value - mean, 2),
      0
    ) / valuesWithNames.length;
    const stdDev = Math.sqrt(variance);

    // Find min and max with company names
    const minItem = sortedValues[0];
    const maxItem = sortedValues[sortedValues.length - 1];

    return {
      count: valuesWithNames.length,
      min: minItem.value,
      minCompany: minItem.name,
      max: maxItem.value,
      maxCompany: maxItem.name,
      mean,
      median: getPercentile(sortedValues, 50).value,
      q1: getPercentile(sortedValues, 25).value,
      q3: getPercentile(sortedValues, 75).value,
      stdDev,
      format
    };
  };

  const stats = calculateStats();

  return (
    <Drawer
      title={`Statistical Analysis - ${title}`}
      placement="right"
      onClose={onClose}
      open={visible}
      width={400}
    >
      {stats ? (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Basic Metrics</h3>
            <p className="text-gray-700">Count: {stats.count}</p>
            <p className="text-gray-700">
              Minimum: {formatStatValue(stats.min, stats.format)} ({stats.minCompany})
            </p>
            <p className="text-gray-700">
              Maximum: {formatStatValue(stats.max, stats.format)} ({stats.maxCompany})
            </p>
          </div>

          <Divider />

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Central Tendency</h3>
            <p className="text-gray-700">Mean: {formatStatValue(stats.mean, stats.format)}</p>
            <p className="text-gray-700">Median: {formatStatValue(stats.median, stats.format)}</p>
          </div>

          <Divider />

          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">Distribution</h3>
            <p className="text-gray-700">
              Q1 (25th percentile): {formatStatValue(stats.q1, stats.format)}
            </p>
            <p className="text-gray-700">
              Q3 (75th percentile): {formatStatValue(stats.q3, stats.format)}
            </p>
            <p className="text-gray-700">
              Standard Deviation: {formatStatValue(stats.stdDev, stats.format)}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">No statistical data available</p>
      )}
    </Drawer>
  );
};

// Colors for Touchpoints
const colors = {
  'Plan Sponsor': '#1E40AF',
  'Recordkeeper': '#3B82F6',
  TPA: '#60A5FA',
  Participants: '#93C5FD',
};

// Render Pagination Controls
const renderPagination = (currentPage, setCurrentPage, totalItems, itemsPerPage) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  return (
    <div className="mt-6 flex justify-center items-center gap-4">
      <button
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200
            rounded-xl transition-all duration-200 hover:bg-blue-50 disabled:opacity-50
            disabled:cursor-not-allowed disabled:hover:bg-white flex items-center gap-2
            hover:border-blue-300 disabled:hover:border-blue-200 shadow-sm"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 12L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Previous
      </button>

      <div className="flex items-center gap-2 text-sm font-medium">
        <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg">{currentPage}</span>
        <span className="text-gray-500">of</span>
        <span className="text-gray-700">{totalPages}</span>
      </div>

      <button
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200
            rounded-xl transition-all duration-200 hover:bg-blue-50 disabled:opacity-50
            disabled:cursor-not-allowed disabled:hover:bg-white flex items-center gap-2
            hover:border-blue-300 disabled:hover:border-blue-200 shadow-sm"
      >
        Next
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 12L10 8L6 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

// Get Maximum Value for Y-Axis
const getMaxValue = (data, key) => {
  if (!data || !Array.isArray(data) || data.length === 0) return 0;
  return Math.ceil(
    Math.max(
      ...data.map((item) => (typeof item[key] === 'number' ? item[key] : 0))
    )
  );
};

// PaginatedChart Component
const PaginatedChart = ({ data, renderChart, itemsPerPage, valueKey }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Handle empty or undefined data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data to display</p>
      </div>
    );
  }

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate max value using the provided valueKey
  const maxValue = getMaxValue(data, valueKey);

  return (
    <>
      {renderChart(paginatedData, maxValue)}
      {data.length > itemsPerPage &&
        renderPagination(currentPage, setCurrentPage, data.length, itemsPerPage)}
    </>
  );
};

// RevenuePerHourChart Component
const RevenuePerHourChart = React.forwardRef(({ dashboardData, timeData }, ref) => {
  const chartData = useMemo(() => {
    if (!timeData?.time_spent || !dashboardData?.plan_performance) return [];

    return dashboardData.plan_performance.map((plan) => {
      const timeSpent = timeData.time_spent[plan.company_name] || 0;
      return {
        name: abbreviateName(plan.company_name),
        fullName: plan.company_name,
        revenuePerHour: timeSpent > 0 ? plan.advisor_yearly_compensation / timeSpent : 0,
      };
    });
  }, [dashboardData, timeData]);

  if (!chartData.length) return null;

  const RevenuePerHourTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 backdrop-blur-md border border-blue-200/80 p-3 rounded-xl
          shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
          ring-1 ring-blue-100/30">
          <p className="font-semibold text-gray-800">{payload[0]?.payload?.fullName || label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`Revenue Generated Per Hour: $${Math.round(entry.value)}/hour`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={ref}>
      <ChartCard
        title="Revenue Per Hour ($)"
        description="Shows the revenue generated per hour spent on each plan"
        className="col-span-full mb-8"
        data={chartData}
        valueKey="revenuePerHour"
      >
        {(filteredData, barColor, itemsPerPage) => (
          <PaginatedChart
            data={filteredData}
            itemsPerPage={itemsPerPage}
            valueKey="revenuePerHour"
            renderChart={(paginatedData, maxValue) => (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paginatedData}>
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, maxValue]} />
                  <RechartsTooltip content={<RevenuePerHourTooltip />} />
                  <Bar dataKey="revenuePerHour" fill={barColor} />
                </BarChart>
              </ResponsiveContainer>
            )}
          />
        )}
      </ChartCard>
    </div>
  );
});

// Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const { isCustomerView } = useCompany();

  // Redirect customer users away from dashboard
  useEffect(() => {
    if (isCustomerView) {
      navigate('/spreadsheet/client-list');
    }
  }, [isCustomerView, navigate]);

  // Set the initial selected year – ensure it's a number rather than a string
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStakeholders] = useState([
    'Plan Sponsor',
    'Recordkeeper',
    'TPA',
    'Participants',
  ]);
  
  // Use the React Query hook for all dashboard data
  const {
    dashboardData,
    timeData,
    clientListData,
    touchpointData,
    requirementsData: requirementChecklistData,
    previousYearData,
    previousYearClientList,
    availableYears,
    isLoading: loading,
    error,
  } = useDashboardQuery(selectedYear);
  
  const availableYearsRef = useRef(availableYears);

  // Refs for each major section
  const overviewRef = useRef(null);
  const aumDistributionRef = useRef(null);
  const yearlyRevenueRef = useRef(null);
  const accountBalanceRef = useRef(null);
  const participationRateRef = useRef(null);
  const touchpointsRef = useRef(null);
  const timeAnalysisRef = useRef(null);
  const revenuePerHourRef = useRef(null);
  const numberOfParticipantsRef = useRef(null);
  const loanWithdrawalUsageRef = useRef(null);
  const employeesRegisteredOnlineRef = useRef(null);
  const employeesOptingForPaperStatementsRef = useRef(null);
  const interactionRateRef = useRef(null);
  const numberOfPlansAvailableRef = useRef(null);
  const employeesBuildTheirOwnPortfolioRef = useRef(null);
  const useTargetOrInvestmentModelsRef = useRef(null);
  const checklistRequirementsRef = useRef(null);
  const deferralRateRef = useRef(null);
  const requirementsTrackingRef = useRef(null);


  // Define sections for the floating nav
  const sections = [
    { title: 'Overview', ref: overviewRef },
    { title: 'Plan Touchpoints', ref: touchpointsRef },
    { title: 'Revenue Per Hour', ref: revenuePerHourRef },
    // { title: 'Requirements Tracking', ref: requirementsTrackingRef },
    { title: 'Yearly Revenue', ref: yearlyRevenueRef },
    { title: 'Time Analysis', ref: timeAnalysisRef },
    { title: 'Participation Rate', ref: participationRateRef },
    { title: 'Average Account Balance', ref: accountBalanceRef },
    { title: 'AUM Distribution', ref: aumDistributionRef },
    { title: 'Number of Participants', ref: numberOfParticipantsRef },
    { title: 'Loan Withdrawal Usage', ref: loanWithdrawalUsageRef },
    { title: 'Employees Registered Online', ref: employeesRegisteredOnlineRef },
    { title: 'Employees Opting for Paper Statements', ref: employeesOptingForPaperStatementsRef },
    { title: 'Interaction Rate', ref: interactionRateRef },
    { title: 'Number of Plans Available', ref: numberOfPlansAvailableRef },
    { title: 'Employees Building Their Own Portfolio', ref: employeesBuildTheirOwnPortfolioRef },
    { title: 'Use Target or Investment Models', ref: useTargetOrInvestmentModelsRef },
    { title: 'Checklist Requirements', ref: checklistRequirementsRef },
    { title: 'Deferral Rate', ref: deferralRateRef },
  ];

  // Update availableYearsRef when availableYears changes
  useEffect(() => {
    availableYearsRef.current = availableYears;
  }, [availableYears]);
  
  // Process touchpoint data for display
  const processedTouchpointData = useMemo(() => {
    if (!touchpointData?.companies) return null;
    
    return {
      data: touchpointData.companies.map(company => ({
        'Company Name': company.name,
        'Quarter': 'Total For Stakeholder',
        'Plan Sponsor': company.stakeholder_breakdown['Plan Sponsor'] || 0,
        'Recordkeeper': (
          company.stakeholder_breakdown['Recordkeeper'] || 
          company.stakeholder_breakdown['RecordKeeper'] || 
          0
        ),
        'TPA': company.stakeholder_breakdown['TPA'] || 0,
        'Participants': (
          company.stakeholder_breakdown['Participants'] || 
          company.stakeholder_breakdown['Participant'] || 
          0
        )
      }))
    };
  }, [touchpointData]);


  // If still loading or no dashboard data, show spinner
  if (loading || !dashboardData) {
    return <LoadingSpinner />;
  }

  // If there's an error, show error message
  if (error) {
    return <ErrorDisplay message={error} />;
  }

  // Calculate Metrics with null checks
  const calculateMetric = (metric, data) => {
    if (!data || !data.plan_performance) return 0;
    return data.plan_performance.reduce((sum, plan) => sum + plan[metric], 0);
  };

  const totalAUM = calculateMetric('plan_size', dashboardData);
  const totalParticipants = calculateMetric('number_of_participants', dashboardData);
  const yearlyRevenue = calculateMetric('advisor_yearly_compensation', dashboardData);

  const previousTotalAUM = calculateMetric('plan_size', previousYearData);
  const previousTotalParticipants = calculateMetric('number_of_participants', previousYearData);
  const previousYearlyRevenue = calculateMetric('advisor_yearly_compensation', previousYearData);

  const calculateChange = (current, previous) => {
    if (!previousYearData || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const aumChange = calculateChange(totalAUM, previousTotalAUM);
  const participantsChange = calculateChange(totalParticipants, previousTotalParticipants);
  const revenueChange = calculateChange(yearlyRevenue, previousYearlyRevenue);

  // Update StatCard for Number of Plans with null check
  const numberOfPlans = clientListData?.length || 0;
  const previousNumberOfPlans = previousYearClientList?.length || 0;
  const plansChange = calculateChange(numberOfPlans, previousNumberOfPlans);

  // Prepare Data for TreeMap
  const treeMapData = dashboardData.plan_performance
    .map((plan) => ({
      name: abbreviateName(plan.company_name),
      fullName: plan.company_name,
      size: plan.plan_size,
    }))
    .sort((a, b) => b.size - a.size); // Sort in descending order

  // Prepare Data for Other Charts
  const advisorYearlyRevenue = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    revenue: plan.advisor_yearly_compensation,
  }));

  const averageAccountBalance = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    balance: plan.average_account_balance,
  }));

  const participationRate = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.participation_rate * 100,
  }));

  // Additional Charts Data
  const numberOfParticipantsData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    participants: plan.number_of_participants,
  }));

  const loanWithdrawalData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.loan_withdrawal_usage * 100, // Convert to percentage
  }));

  const onlineRegistrationData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.employees_registered_online * 100, // Convert to percentage
  }));

  const paperStatementsData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.employees_opting_for_paper_statements * 100, // Convert to percentage
  }));

  const interactionRateData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.interaction_rate,
  }));

  const plansAvailableData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    count: plan.number_of_plans_available,
  }));

  const ownPortfolioData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.employees_build_their_own_portfolio * 100, // Convert to percentage
  }));

  const investmentModelsData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.use_target_or_investment_models * 100, // Convert to percentage
  }));

  const deferralRateData = dashboardData.plan_performance.map((plan) => ({
    name: abbreviateName(plan.company_name),
    fullName: plan.company_name,
    rate: plan.deferral_rate,
  }));

  // TouchpointChart Component
  const TouchpointChart = React.forwardRef(({ data }, ref) => {
    // console.log('TouchpointChart data:', data);
    const processData = (rawData) => {
      if (!rawData || !rawData.data || rawData.data.length === 0) return [];

      return rawData.data
        .filter((row) => row.Quarter === 'Total For Stakeholder')
        .map((row) => ({
          name: abbreviateName(row['Company Name']),
          fullName: row['Company Name'],
          ...selectedStakeholders.reduce(
            (acc, stakeholder) => ({
              ...acc,
              [stakeholder]: row[stakeholder] || 0,
            }),
            {}
          ),
          total: selectedStakeholders.reduce((sum, stakeholder) => sum + (row[stakeholder] || 0), 0),
        }));
    };

    TouchpointChart.whyDidYouRender = true;

    if (!data) return <LoadingSpinner />;

    const allData = processData(data);
    if (allData.length === 0) return null;


      // Custom tooltip for Touchpoints by Stakeholder chart
    const TouchpointsTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white/90 backdrop-blur-md border border-blue-200/80 p-3 rounded-xl
            shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
            ring-1 ring-blue-100/30">
            <p className="font-semibold text-gray-800">{payload[0]?.payload?.fullName || label}</p>
            {payload.map((entry, index) => (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {`${entry.name}: ${entry.value}`} {/* Removed currency formatting */}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    return (
      <div ref={ref}>
        <ChartCard
          title="Plan Touchpoints by Stakeholder"
          description="Displays the number of interactions with different stakeholders for each plan"
          className="col-span-full mb-8"
          data={allData}
          valueKey="total"
        >
          {(filteredData, _, itemsPerPage) => (
            <PaginatedChart
              data={filteredData}
              itemsPerPage={itemsPerPage}
              valueKey="total"
              renderChart={(paginatedData, maxValue) => (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={paginatedData}>
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, maxValue]} />
                    <RechartsTooltip content={<TouchpointsTooltip />} />
                    <Legend />
                    {selectedStakeholders.map((stakeholder) => (
                      <Bar
                        key={stakeholder}
                        dataKey={stakeholder}
                        stackId="a"
                        fill={colors[stakeholder]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            />
          )}
        </ChartCard>
      </div>
    );
  });

  // TimeChart Component
  const TimeChart = React.forwardRef(({ data }, ref) => {
    // Move useMemo before any conditional returns
    const chartData = useMemo(() => {
      if (!data || !data.time_spent) return [];

      return Object.entries(data.time_spent).map(([companyName, timeSpent]) => ({
        'Company Name': abbreviateName(companyName),
        fullName: companyName,
        'Time Spent (Hours)': timeSpent,
      }));
    }, [data]);

    // Move the null check after the useMemo
    if (!data || !data.time_spent) return null;
    if (chartData.length === 0) return null;

    // Custom tooltip for Time Analysis chart
    const TimeTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white/90 backdrop-blur-md border border-blue-200/80 p-3 rounded-xl
            shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
            ring-1 ring-blue-100/30">
            <p className="font-semibold text-gray-800">{payload[0]?.payload?.fullName || label}</p>
            {payload.map((entry, index) => (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {`${entry.name}: ${(entry.value).toFixed(2)}`}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    return (
      <div ref={ref}>
        <ChartCard
          title="Time Analysis"
          description="Time spent managing each plan throughout the year"
          className="col-span-full mb-8"
          data={chartData.map(item => ({
            ...item,
            'Time Spent (Hours)': item['Time Spent (Hours)'] / 60
          }))}
          valueKey="Time Spent (Hours)"
        >
          {(filteredData, barColor, itemsPerPage) => (
            <PaginatedChart
              data={filteredData}
              itemsPerPage={itemsPerPage}
              valueKey="Time Spent (Hours)"
              renderChart={(paginatedData, maxValue) => (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={paginatedData}>
                    <XAxis dataKey="Company Name" />
                    <YAxis domain={[0, maxValue]} />
                    <RechartsTooltip content={<TimeTooltip />} />
                    <Bar dataKey="Time Spent (Hours)" fill={barColor} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            />
          )}
        </ChartCard>
      </div>
    );
  });

  // Render the Dashboard
  return (
    <div className="min-h-screen p-2 sm:p-8">
      <FloatingNav
        sections={sections}
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={(value) => setSelectedYear(value)}
      />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-[1920px] mx-auto"
      >
        {/* Overview Section */}
        <motion.div variants={itemVariants} className="mb-8" ref={overviewRef}>
          <SectionTitle>Plan Management Dashboard</SectionTitle>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <StatCard
            title="Total AUM"
            value={totalAUM}
            icon={DollarSign}
            change={aumChange}
            format="currency"
          />
          <StatCard
            title="Number of Plans"
            value={numberOfPlans}
            icon={PieChartIcon}
            change={plansChange}
            format="number"
          />
          <StatCard
            title="Total Participants"
            value={totalParticipants}
            icon={Users}
            change={participantsChange}
            format="number"
          />
          <StatCard
            title="Yearly Revenue"
            value={yearlyRevenue}
            icon={TrendingUp}
            change={revenueChange}
            format="currency"
          />
        </motion.div>

        {/* 1. Plan Touchpoints */}
        {processedTouchpointData && <TouchpointChart data={processedTouchpointData} ref={touchpointsRef} />}

        {/* 2. Revenue Per Hour */}
        <RevenuePerHourChart
          ref={revenuePerHourRef}
          dashboardData={dashboardData}
          timeData={timeData}
        />

        {/* 3. Requirements Tracking (moved from position 17) */}
        {/* {requirementChecklistData?.data && requirementChecklistData.data.length > 0 && (
          <ChartErrorBoundary>
            <RequirementsTrackingChart 
              ref={requirementsTrackingRef}
              data={requirementChecklistData.data}
            />
          </ChartErrorBoundary>
        )} */}

        {/* 4. Advisor Yearly Revenue */}
        <div ref={yearlyRevenueRef}>
          <ChartCard
            title="Advisor Yearly Revenue"
            description="Annual revenue breakdown by plan"
            className="col-span-full mb-8"
            data={advisorYearlyRevenue}
            valueKey="revenue"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="revenue"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 5. Time Analysis */}
        {timeData && <TimeChart data={timeData} ref={timeAnalysisRef} />}

        {/* 6. Participation Rate */}
        <div ref={participationRateRef}>
          <ChartCard
            title="Participation Rate"
            description="Percentage of eligible employees participating in each plan"
            className="col-span-full mb-8"
            data={participationRate}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 7. Average Account Balance */}
        <div ref={accountBalanceRef}>
          <ChartCard
            title="Average Account Balance"
            description="Mean account balance for participants in each plan"
            className="col-span-full mb-8"
            data={averageAccountBalance}
            valueKey="balance"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="balance"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="balance" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 8. AUM Distribution */}
        <div ref={aumDistributionRef}>
          <ChartCard
            title="AUM Distribution"
            description="Visual representation of Assets Under Management across all plans"
            className="col-span-full mb-8"
            data={treeMapData}
            valueKey="size"
          >
            {(filteredData) => (
              <div className="relative">
                {filteredData.length > 0 ? (
                  <>
                    <div className="absolute top-0 right-0 bg-white bg-opacity-75 p-2 rounded-bl-md z-10">
                      <span className="font-bold text-blue-600">
                        Total: {formatNumber(calculateTotalSize(filteredData), 'currency')}
                      </span>
                    </div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <ResponsiveContainer width="100%" height={400}>
                        <Treemap
                          data={filteredData}
                          dataKey="size"
                          aspectRatio={4 / 3}
                          stroke="#fff"
                          content={<CustomTreemapContent />}
                        >
                          <RechartsTooltip
                            content={<CustomTreemapTooltip totalSize={calculateTotalSize(filteredData)} />}
                          />
                        </Treemap>
                      </ResponsiveContainer>
                    </motion.div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No data to display</p>
                  </div>
                )}
              </div>
            )}
          </ChartCard>
        </div>

        {/* 9. Number of Participants */}
        <div ref={numberOfParticipantsRef}>
          <ChartCard
            title="Number of Participants"
            description="Total number of participants enrolled in each plan"
            className="col-span-full mb-8"
            data={numberOfParticipantsData}
            valueKey="participants"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="participants"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="participants" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 10. Loan Withdrawal Usage */}
        <div ref={loanWithdrawalUsageRef}>
          <ChartCard
            title="Loan Withdrawal Usage (%)"
            description="Percentage of participants utilizing loan withdrawal options"
            className="col-span-full mb-8"
            data={loanWithdrawalData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 11. Employees Registered Online */}
        <div ref={employeesRegisteredOnlineRef}>
          <ChartCard
            title="Employees Registered Online (%)"
            description="Percentage of employees who have registered for online account access"
            className="col-span-full mb-8"
            data={onlineRegistrationData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 12. Employees Opting for Paper Statements */}
        <div ref={employeesOptingForPaperStatementsRef}>
          <ChartCard
            title="Employees Opting for Paper Statements (%)"
            description="Percentage of participants who prefer paper statements over electronic delivery"
            className="col-span-full mb-8"
            data={paperStatementsData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 13. Interaction Rate */}
        <div ref={interactionRateRef}>
          <ChartCard
            title="Interaction Rate (%)"
            description="Frequency of participant engagement with their retirement accounts"
            className="col-span-full mb-8"
            data={interactionRateData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 14. Number of Plans Available */}
        <div ref={numberOfPlansAvailableRef}>
          <ChartCard
            title="Number of Plans Available"
            description="Total number of investment options available in each plan"
            className="col-span-full mb-8"
            data={plansAvailableData}
            valueKey="count"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="count"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 15. Employees Building Their Own Portfolio */}
        <div ref={employeesBuildTheirOwnPortfolioRef}>
          <ChartCard
            title="Employees Building Their Own Portfolio (%)"
            description="Percentage of participants who customize their investment allocations"
            className="col-span-full mb-8"
            data={ownPortfolioData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 16. Use Target or Investment Models */}
        <div ref={useTargetOrInvestmentModelsRef}>
          <ChartCard
            title="Use Target or Investment Models (%)"
            description="Percentage of participants using pre-defined investment strategies"
            className="col-span-full mb-8"
            data={investmentModelsData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>

        {/* 17. Deferral Rate */}
        <div ref={deferralRateRef}>
          <ChartCard
            title="Deferral Rate (%)"
            description="Average percentage of salary being contributed to retirement plans"
            className="col-span-full mb-8"
            data={deferralRateData}
            valueKey="rate"
          >
            {(filteredData, barColor, itemsPerPage) => (
              <PaginatedChart
                data={filteredData}
                itemsPerPage={itemsPerPage}
                valueKey="rate"
                renderChart={(paginatedData, maxValue) => (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={paginatedData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, maxValue]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="rate" fill={barColor} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              />
            )}
          </ChartCard>
        </div>
      </motion.div>
    </div>
  );
};
// Dashboard.whyDidYouRender = true;

// Add these constants near the top with your other color definitions
const requirementColors = {
  'Completed': '#60A5FA',   
  'Not Completed': '#93C5FD', // Lighter blue
  'In Progress': '#BFDBFE', // Light
  'No Requirements': '#F0F9FF', // Lightest blue
};

// Add this new component after your other chart components
const RequirementsTrackingChart = React.memo(React.forwardRef(({ data }, ref) => {
  // Memoize the processed data
  const processedData = useMemo(() => {
    if (!data) return [];

    // Group by company name
    const groupedByCompany = data.reduce((acc, item) => {
      if (!acc[item['Company Name']]) {
        acc[item['Company Name']] = {
          name: abbreviateName(item['Company Name']),
          fullName: item['Company Name'],
          'Not Completed': 0,
          'In Progress': 0,
          'Completed': 0,
          total: 0,
        };
      }
      
      const status = item.Complete || 'Not Completed';
      acc[item['Company Name']][status] = (acc[item['Company Name']][status] || 0) + (item.Count || 0);
      acc[item['Company Name']].total += (item.Count || 0);
      
      return acc;
    }, {});

    return Object.values(groupedByCompany);
  }, [data]);

  // Early return if no data
  if (!data || processedData.length === 0) {
    return null;
  }

  // Custom tooltip specifically for requirements tracking
  const RequirementsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 backdrop-blur-md border border-blue-200/80 p-3 rounded-xl
          shadow-[0_10px_40px_-5px_rgba(0,0,0,0.1)]
          ring-1 ring-blue-100/30">
          <p className="font-semibold text-gray-800">{payload[0]?.payload?.fullName || label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`} {/* Removed currency formatting */}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={ref}>
      <ChartCard
        title="Requirements Tracking"
        description="Status of compliance and administrative requirements for each plan"
        className="col-span-full mb-8"
        data={processedData}
        valueKey="total"
      >
        {(filteredData, _, itemsPerPage) => (
          <PaginatedChart
            data={filteredData}
            itemsPerPage={itemsPerPage}
            valueKey="total"
            renderChart={(paginatedData, maxValue) => (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={paginatedData}>
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, maxValue]} />
                  <RechartsTooltip content={<RequirementsTooltip />} />
                  <Legend />
                  {['Completed', 'In Progress', 'Not Completed'].map((status) => (
                    <Bar
                      key={status}
                      dataKey={status}
                      stackId="a"
                      fill={requirementColors[status]}
                      name={status}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          />
        )}
      </ChartCard>
    </div>
  );
}));

export default Dashboard;